<script setup lang="ts">
import { computed, nextTick, onBeforeUnmount, onMounted, shallowRef, watch } from "vue";
import { useI18n } from "vue-i18n";
import { Check, ChevronDown, ChevronUp, Circle, GripVertical, LoaderCircle, Map } from "lucide-vue-next";
import { api } from "@/services/api";

const props = defineProps<{
  skillId: string;
  currentStage?: string;
  running?: boolean;
  complete?: boolean;
}>();
const { t } = useI18n();
const positionStorageKey = "jaohui.skill-map-position";
const skill = shallowRef<{ id: string; name: string; planningStages: string[] } | null>(null);
const expanded = shallowRef(Boolean(props.running));
const mapElement = shallowRef<HTMLElement | null>(null);
const desktop = shallowRef(false);
const dragging = shallowRef(false);
const dragPosition = shallowRef<{ left: number; top: number } | null>(null);
let dragState: { pointerId: number; startX: number; startY: number; left: number; top: number } | null = null;

const stages = computed(() => skill.value?.planningStages || []);
const currentIndex = computed(() => {
  if (props.complete) return Math.max(stages.value.length - 1, 0);
  const index = stages.value.indexOf(props.currentStage || "");
  return index >= 0 ? index : 0;
});
const currentLabel = computed(() =>
  props.complete
    ? t("agent.flowComplete")
    : stages.value[currentIndex.value] || t("agent.discoveryStage"),
);
const progressPercent = computed(() => {
  if (props.complete) return 100;
  return stages.value.length > 1
    ? Math.round((currentIndex.value / (stages.value.length - 1)) * 100)
    : 0;
});
const positionStyle = computed(() =>
  desktop.value && dragPosition.value
    ? {
        left: `${dragPosition.value.left}px`,
        top: `${dragPosition.value.top}px`,
        right: "auto",
        bottom: "auto",
        transform: "none",
      }
    : undefined,
);

function clampPosition(position: { left: number; top: number }) {
  const rect = mapElement.value?.getBoundingClientRect();
  if (!rect) return position;
  const padding = 10;
  return {
    left: Math.min(Math.max(position.left, padding), Math.max(padding, window.innerWidth - rect.width - padding)),
    top: Math.min(Math.max(position.top, padding), Math.max(padding, window.innerHeight - rect.height - padding)),
  };
}

function persistPosition() {
  if (!dragPosition.value) return;
  localStorage.setItem(positionStorageKey, JSON.stringify(dragPosition.value));
}

function moveMap(event: PointerEvent) {
  if (!dragState || event.pointerId !== dragState.pointerId) return;
  dragPosition.value = clampPosition({
    left: dragState.left + event.clientX - dragState.startX,
    top: dragState.top + event.clientY - dragState.startY,
  });
}

function stopDragging(event?: PointerEvent) {
  if (event && dragState && event.pointerId !== dragState.pointerId) return;
  dragState = null;
  dragging.value = false;
  window.removeEventListener("pointermove", moveMap);
  window.removeEventListener("pointerup", stopDragging);
  window.removeEventListener("pointercancel", stopDragging);
  persistPosition();
}

function startDragging(event: PointerEvent) {
  if (!desktop.value || event.button !== 0 || !mapElement.value) return;
  const rect = mapElement.value.getBoundingClientRect();
  dragPosition.value = { left: rect.left, top: rect.top };
  dragState = {
    pointerId: event.pointerId,
    startX: event.clientX,
    startY: event.clientY,
    left: rect.left,
    top: rect.top,
  };
  dragging.value = true;
  event.preventDefault();
  window.addEventListener("pointermove", moveMap);
  window.addEventListener("pointerup", stopDragging);
  window.addEventListener("pointercancel", stopDragging);
}

function resetPosition() {
  dragPosition.value = null;
  localStorage.removeItem(positionStorageKey);
}

function moveWithKeyboard(event: KeyboardEvent) {
  if (!desktop.value) return;
  if (event.key === "Home") {
    event.preventDefault();
    resetPosition();
    return;
  }
  const directions: Record<string, [number, number]> = {
    ArrowLeft: [-1, 0],
    ArrowRight: [1, 0],
    ArrowUp: [0, -1],
    ArrowDown: [0, 1],
  };
  const direction = directions[event.key];
  if (!direction || !mapElement.value) return;
  event.preventDefault();
  const rect = mapElement.value.getBoundingClientRect();
  const current = dragPosition.value || { left: rect.left, top: rect.top };
  const distance = event.shiftKey ? 24 : 8;
  dragPosition.value = clampPosition({
    left: current.left + direction[0] * distance,
    top: current.top + direction[1] * distance,
  });
  persistPosition();
}

function syncViewport() {
  desktop.value = window.innerWidth > 700;
  if (desktop.value && dragPosition.value) {
    void nextTick().then(() => {
      if (dragPosition.value) dragPosition.value = clampPosition(dragPosition.value);
    });
  }
}

async function load() {
  skill.value = null;
  if (!props.skillId) return;
  try {
    skill.value = (
      await api<{ skill: { id: string; name: string; planningStages: string[] } }>(
        `/skill-flows/${encodeURIComponent(props.skillId)}`,
      )
    ).skill;
  } catch {
    skill.value = null;
  }
}

watch(() => props.skillId, () => void load(), { immediate: true });
watch(() => props.running, (running) => { expanded.value = Boolean(running); });
watch(() => props.complete, (complete) => { if (complete) expanded.value = false; });
watch(expanded, () => {
  if (!dragPosition.value) return;
  void nextTick().then(() => {
    if (dragPosition.value) dragPosition.value = clampPosition(dragPosition.value);
  });
});

onMounted(() => {
  syncViewport();
  try {
    const stored = JSON.parse(localStorage.getItem(positionStorageKey) || "null");
    if (Number.isFinite(stored?.left) && Number.isFinite(stored?.top)) {
      dragPosition.value = { left: Number(stored.left), top: Number(stored.top) };
      void nextTick().then(() => {
        if (dragPosition.value) dragPosition.value = clampPosition(dragPosition.value);
      });
    }
  } catch {
    localStorage.removeItem(positionStorageKey);
  }
  window.addEventListener("resize", syncViewport);
});

onBeforeUnmount(() => {
  stopDragging();
  window.removeEventListener("resize", syncViewport);
});
</script>

<template>
  <aside ref="mapElement" v-if="stages.length" class="skill-map" :class="{ expanded, running, dragging }" :style="positionStyle" aria-live="polite">
    <div class="skill-map__head">
      <button class="skill-map__drag" type="button" :title="t('agent.dragMap')" :aria-label="t('agent.dragMap')" @pointerdown="startDragging" @dblclick="resetPosition" @keydown="moveWithKeyboard">
        <GripVertical :size="17" />
      </button>
      <button class="skill-map__toggle" type="button" :aria-label="expanded ? t('agent.collapseMap') : t('agent.expandMap')" @click="expanded = !expanded">
        <span class="map-icon"><Map :size="17" /></span>
        <span class="map-title">
          <small>{{ t("agent.skillMap") }} · {{ skill?.name }}</small>
          <b>{{ currentLabel }}</b>
          <em>{{ t("agent.stepCount", { current: currentIndex + 1, total: stages.length }) }}</em>
        </span>
        <ChevronUp v-if="expanded" :size="17" /><ChevronDown v-else :size="17" />
      </button>
    </div>
    <div class="skill-map__track"><i :style="{ width: `${progressPercent}%` }" /></div>
    <div v-if="expanded" class="skill-map__body">
      <ol>
        <li v-for="(stage, index) in stages" :key="`${index}-${stage}`" :class="{ done: complete || index < currentIndex, current: !complete && index === currentIndex, upcoming: !complete && index > currentIndex }">
          <span class="node"><Check v-if="complete || index < currentIndex" :size="12" /><LoaderCircle v-else-if="index === currentIndex && running" :size="13" class="spin" /><Circle v-else :size="11" /></span>
          <span><small>{{ String(index + 1).padStart(2, "0") }}</small><b>{{ stage }}</b></span>
        </li>
      </ol>
    </div>
  </aside>
</template>

<style scoped>
.skill-map{position:fixed;top:50%;right:18px;bottom:auto;z-index:18;width:min(360px,calc(100vw - 28px));overflow:hidden;transform:translateY(-50%);border:1px solid #bfd2d9;background:#fff;box-shadow:0 12px 32px rgba(25,45,55,.16)}.skill-map.dragging{user-select:none;box-shadow:0 18px 44px rgba(25,45,55,.24)}.skill-map__head{display:grid;grid-template-columns:24px minmax(0,1fr);align-items:stretch;min-height:66px;background:#f7fafb}.skill-map__drag,.skill-map__toggle{border:0;background:transparent;color:var(--ink)}.skill-map__drag{display:grid;width:24px;place-items:center;padding:0;color:#7d9198;cursor:grab;touch-action:none}.skill-map__drag:active,.skill-map.dragging .skill-map__drag{cursor:grabbing}.skill-map__drag:focus-visible,.skill-map__toggle:focus-visible{outline:2px solid #2c87aa;outline-offset:-2px}.skill-map__toggle{display:grid;grid-template-columns:30px minmax(0,1fr) 22px;align-items:center;gap:8px;width:100%;padding:9px 10px 9px 4px;text-align:left;cursor:pointer}.map-icon{display:grid;width:28px;height:28px;place-items:center;background:#e7f1f4;color:#26718d}.map-title{display:grid;min-width:0;grid-template-columns:minmax(0,1fr) auto;align-items:center;gap:1px 8px}.map-title small{grid-column:1/-1;overflow:hidden;color:var(--muted);font-size:10px;text-overflow:ellipsis;white-space:nowrap}.map-title b{overflow:hidden;font-size:13px;text-overflow:ellipsis;white-space:nowrap}.map-title em{color:#287590;font-size:10px;font-style:normal;white-space:nowrap}.skill-map__track{height:3px;background:#e7eeee}.skill-map__track i{display:block;height:100%;background:#2c87aa;transition:width .25s ease}.skill-map__body{max-height:min(56vh,520px);overflow:auto;padding:13px 14px 15px}.skill-map ol{margin:0;padding:0;list-style:none}.skill-map li{display:grid;grid-template-columns:24px minmax(0,1fr);gap:8px;position:relative;min-height:42px;color:var(--muted)}.skill-map li:not(:last-child)::before{content:"";position:absolute;left:11px;top:20px;bottom:0;width:2px;background:#dfe7e9}.skill-map li.done::before{background:#59a482}.node{z-index:1;display:grid;width:24px;height:24px;place-items:center;border:2px solid #d7e1e4;border-radius:50%;background:#fff;color:#90a0a6}.skill-map li.done .node{border-color:#59a482;background:#59a482;color:#fff}.skill-map li.current .node{border-color:#2c87aa;color:#2c87aa;box-shadow:0 0 0 4px #e8f4f7}.skill-map li>span:last-child{display:grid;grid-template-columns:24px minmax(0,1fr);align-items:center;min-width:0;padding:2px 0 12px}.skill-map li small{color:#9aa7ab;font-size:10px}.skill-map li b{font-size:12px;font-weight:600}.skill-map li.current b{color:#205f77}.skill-map li.done b{color:#46745f}.skill-map li.upcoming{opacity:.72}.spin{animation:spin .9s linear infinite}@keyframes spin{to{transform:rotate(360deg)}}
@media(max-width:700px){.skill-map{top:calc(var(--mobile-topbar-height,56px) + 8px)!important;right:8px!important;bottom:auto!important;left:auto!important;z-index:28;width:calc(100vw - 16px);transform:none!important}.skill-map:not(.expanded){width:min(240px,calc(100vw - 16px))}.skill-map__head{grid-template-columns:minmax(0,1fr);min-height:54px}.skill-map__drag{display:none}.skill-map__toggle{padding:7px 9px}.skill-map__body{max-height:48vh}.map-title small{max-width:170px}}
@media(prefers-reduced-motion:reduce){.spin{animation:none}.skill-map__track i{transition:none}}
</style>
